import React, { useCallback, useEffect, useState } from "react";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import CommonFormActions from "../../components/common/CommonFormActions";
import CommonModal from "../../components/common/CommonModal";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import CommonTable from "../../components/common/CommonTable";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";
import Swal from "sweetalert2";
import { hasPermission } from "../../utils/permissions";
import "./EmployeeList.css";

function EmployeeList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleEmployee, setSelectedRoleEmployee] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [planSummary, setPlanSummary] = useState(null);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await api.get("/employees");
      if (response.data.success) {
        setEmployees(response.data.data);
        setPlanSummary(response.data.meta?.plan_summary || null);
      }
      if (hasPermission(user, "roles_permissions.manage")) {
        const roleResponse = await api.get("/roles-permissions/roles");
        if (roleResponse.data.success) {
          setAvailableRoles(roleResponse.data.data || []);
        }
      }
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load employees",
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleView = async (employeeId) => {
    try {
      const response = await api.get(`/employees/${employeeId}`);
      if (response.data.success) {
        setSelectedEmployee(response.data.data);
        setShowViewModal(true);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load employee details",
      });
    }
  };

  const handleEdit = (employeeId) => {
    navigate(`/employees/edit/${employeeId}`);
  };

  const openAssignRoleModal = (employee) => {
    setSelectedRoleEmployee(employee);
    setSelectedRoleId(employee.company_access_role_id || "");
    setShowAssignRoleModal(true);
  };

  const handleAssignRole = async () => {
    if (!selectedRoleEmployee) return;

    try {
      const response = await api.put(
        `/employees/${selectedRoleEmployee.id}/access-role`,
        {
          company_access_role_id: selectedRoleId || null,
        },
      );

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Updated",
          text: response.data.message,
          timer: 1800,
          showConfirmButton: false,
        });
        setShowAssignRoleModal(false);
        fetchEmployees();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to assign role.",
      });
    }
  };

  const handleDelete = async (employeeId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this employee?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await api.delete(`/employees/${employeeId}`);
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: response.data.message,
            timer: 2000,
            showConfirmButton: false,
          });
          fetchEmployees();
        }
      } catch (error) {
        const message =
          error.response?.data?.message || "Failed to delete employee";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: message,
        });
      }
    }
  };

  const getEmploymentStatus = (statusId) => {
    const statuses = {
      1: "Full Time Permanent",
      2: "Full Time Contract",
      3: "Full Time Probation",
      4: "Part Time Contract",
      5: "Freelance",
      6: "Intern",
    };
    return statuses[statusId] || "N/A";
  };

  const getGender = (genderId) => {
    return genderId === 0 ? "Male" : "Female";
  };

  const getMaritalStatus = (statusId) => {
    const statuses = {
      0: "Single",
      1: "Married",
      2: "Divorced",
      3: "Widowed",
    };
    return statuses[statusId] || "N/A";
  };

  const detailSections = selectedEmployee
    ? [
        {
          title: "Personal Information",
          fields: [
            { label: "First Name", value: selectedEmployee.first_name },
            { label: "Middle Name", value: selectedEmployee.middle_name },
            { label: "Last Name", value: selectedEmployee.last_name },
            { label: "Email", value: selectedEmployee.email },
            { label: "Contact Number", value: selectedEmployee.phone_number },
            {
              label: "Gender",
              value: getGender(selectedEmployee.gender),
            },
            {
              label: "Marital Status",
              value: getMaritalStatus(selectedEmployee.marital_status),
            },
            { label: "Nationality", value: selectedEmployee.nationality },
            { label: "Birth Date", value: selectedEmployee.birth_date },
            {
              label: "Father's Name",
              value: selectedEmployee.fathers_name,
            },
            {
              label: "Mother's Name",
              value: selectedEmployee.mothers_name,
            },
            {
              label: "Spouse Name",
              value: selectedEmployee.spouse_name,
            },
            { label: "Address", value: selectedEmployee.address },
            { label: "State", value: selectedEmployee.state },
            { label: "City", value: selectedEmployee.city },
            { label: "Pincode", value: selectedEmployee.pincode },
          ],
        },
        {
          title: "Employment Details",
          fields: [
            {
              label: "Access Role",
              value:
                selectedEmployee.company_access_role?.name ||
                "Default Employee Access",
            },
            {
              label: "Department",
              value: selectedEmployee.user_department?.department_name,
            },
            {
              label: "Designation",
              value:
                selectedEmployee.user_designation?.designation_detail
                  ?.designation_name,
            },
            {
              label: "Employment Status",
              value: getEmploymentStatus(selectedEmployee.employment_status),
            },
            { label: "Joining Date", value: selectedEmployee.hired_date },
            { label: "Tax Regime", value: selectedEmployee.tax_regime },
          ],
        },
        {
          title: "Bank Details",
          fields: [
            {
              label: "Bank Account Number",
              value: selectedEmployee.bank_account_number,
            },
            {
              label: "IFSC Code",
              value: selectedEmployee.bank_ifsc_code,
            },
            {
              label: "Bank Name",
              value: selectedEmployee.bank_name,
            },
            {
              label: "Branch Name",
              value: selectedEmployee.bank_branch_name,
            },
          ],
        },
      ]
    : [];

  const employeeDocuments = selectedEmployee?.employee_documents || [];
  const documentTypeLabels = {
    pan_card: "PAN Card",
    aadhaar_card: "Aadhaar Card",
    bank_proof: "Bank Proof",
    passport: "Passport",
  };

  const employeeColumns = [
    {
      header: "Sr.No.",
      render: (_, index) => index + 1,
    },
    {
      header: "Emp. ID",
      key: "code",
    },
    {
      header: "Name",
      render: (employee) => (
        <div className="employee-name">
          {employee.first_name} {employee.middle_name} {employee.last_name}
        </div>
      ),
    },
    {
      header: "Designation",
      render: (employee) =>
        employee.user_designation?.designation_detail?.designation_name ||
        "N/A",
    },
    {
      header: "Access Role",
      render: (employee) => employee.company_access_role?.name || "Default",
    },
    {
      header: "Email",
      key: "email",
    },
    {
      header: "Phone",
      key: "phone_number",
    },
    {
      header: "Status",
      render: (employee) => (
        <span
          className={`status-badge ${employee.is_active ? "active" : "inactive"}`}
        >
          {employee.is_active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      header: "Action",
      render: (employee) => (
        <CommonActionMenu
          ariaLabel={`Actions for employee ${employee.code}`}
          actions={[
            {
              label: "View",
              icon: "fa-solid fa-eye",
              onClick: () => handleView(employee.id),
            },
            {
              label: "Edit",
              icon: "fa-solid fa-edit",
              onClick: () => handleEdit(employee.id),
            },
            ...(hasPermission(user, "roles_permissions.manage")
              ? [
                  {
                    label: "Assign Role",
                    icon: "fa-solid fa-user-shield",
                    onClick: () => openAssignRoleModal(employee),
                  },
                ]
              : []),
            {
              label: "Delete",
              icon: "fa-solid fa-trash",
              variant: "danger",
              onClick: () => handleDelete(employee.id),
            },
          ]}
        />
      ),
    },
  ];

  const handleImport = async () => {

    if (!importFile) {
      setImportError("Please select an Excel file to import.");
      return;
    }

    const allowedTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];
    if (
      !allowedTypes.includes(importFile.type) &&
      !importFile.name.match(/\.(xlsx|xls|csv)$/i)
    ) {
      setImportError("Only Excel (.xlsx, .xls) or CSV files are allowed.");
      return;
    }

    setImporting(true);
    setImportError("");

    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("company_id", user?.companyid || user?.company_id);

      const res = await api.post("/import-employee", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: "Import Complete",
          text: res.data.message,
          confirmButtonColor: "#6557ff",
        });
        setShowImportModal(false);
        fetchEmployees(); // ← refresh your employee list
      }
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to import employees";
      setImportError(msg);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading employees..." /></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="employee-list-page">
        <div className="page-header">
          <h1 className="page-title">Employee List</h1>
          <div className="header-actions">
            <button
              className="btn-download"
              onClick={() => window.open("/employeelist-pdf", "_blank")}
            >
              <i className="fa-solid fa-file-pdf"></i>
              <span>Download</span>
            </button>
            {hasPermission(user, "people.manage") && (
              <button
                className="btn-import"
                onClick={() => {

                  setImportFile(null);
                  setImportError("");
                  setShowImportModal(true);
                }}
              >
                <i className="fa-solid fa-upload"></i>
                <span>Import Employee</span>
              </button>
            )}
            {hasPermission(user, "people.manage") && (
              <button
                className="btn-add"
                onClick={() => {

                  navigate("/employees/add");
                }}
              >
                <i className="fa-solid fa-plus"></i>
                <span>Add Employee</span>
              </button>
            )}
          </div>
        </div>

        {planSummary && (
          <div className="employee-plan-banner ok">
            <div className="employee-plan-copy">
              <span className="employee-plan-label">Subscription usage</span>
              <strong>
                {planSummary.plan_name || "Active Plan"}: {planSummary.employee_count} active employees
              </strong>
              <p>
                {planSummary.included_employees} included, {planSummary.additional_employees} billed as add-ons at Rs {Number(planSummary.add_on_rate || 0).toLocaleString("en-IN")} each.
              </p>
            </div>

          </div>
        )}

        <div className="employee-card">
          <div className="card-header-section">
            <div className="employee-count">
              Total Employees: <span>{employees.length}</span>
            </div>
          </div>

          <CommonTable
            columns={employeeColumns}
            data={employees}
            tableClassName="employee-table"
            wrapperClassName="table-responsive"
            noDataClassName="no-data"
            emptyMessage="No employees found"
            searchPlaceholder="Search by name, code, email, or designation..."
            getSearchText={(employee) =>
              [
                employee.code,
                employee.first_name,
                employee.middle_name,
                employee.last_name,
                employee.email,
                employee.user_designation?.designation_detail?.designation_name,
              ]
                .filter(Boolean)
                .join(" ")
            }
          />
        </div>
      </div>

      {/* View Employee Modal */}
      {showViewModal && selectedEmployee && (
        <CommonModal
          onClose={() => setShowViewModal(false)}
          title="Employee Details"
          overlayClassName="modal-overlay"
          contentClassName="modal-container view-modal"
          headerClassName="modal-header"
          bodyClassName="modal-body"
          closeButtonClassName="modal-close"
        >
          <div className="employee-detail-layout">
            <div className="employee-detail-hero">
              <div className="employee-detail-avatar">
                {(selectedEmployee.first_name?.[0] || "").toUpperCase()}
                {(selectedEmployee.last_name?.[0] || "").toUpperCase()}
              </div>
              <div className="employee-detail-intro">
                <h3>
                  {selectedEmployee.first_name} {selectedEmployee.middle_name}{" "}
                  {selectedEmployee.last_name}
                </h3>
                <p>{selectedEmployee.email || "No email available"}</p>
                <div className="employee-detail-meta">
                  <span className="employee-detail-chip">
                    {selectedEmployee.code || "N/A"}
                  </span>
                  <span
                    className={`status-badge ${
                      selectedEmployee.is_active ? "active" : "inactive"
                    }`}
                  >
                    {selectedEmployee.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            {detailSections.map((section) => (
              <section key={section.title} className="detail-section">
                <div className="section-divider">
                  <h6>{section.title}</h6>
                </div>
                <div className="detail-grid">
                  {section.fields.map((field) => (
                    <div key={field.label} className="detail-card">
                      <span className="detail-label">{field.label}</span>
                      <span className="detail-value text-capitalize">
                        {field.value || "N/A"}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            ))}

            <section className="detail-section">
              <div className="section-divider">
                <h6>Documents</h6>
              </div>
              <div className="detail-grid">
                {Object.entries(documentTypeLabels).map(([key, label]) => {
                  const document = employeeDocuments.find(
                    (item) => item.document_type === key,
                  );
                  const status = document?.approval_status || "missing";

                  return (
                    <div key={key} className="detail-card document-detail-card">
                      <span className="detail-label">{label}</span>
                      <span className="detail-value">
                        {document?.original_name || "Not uploaded"}
                      </span>
                      <span className={`employee-document-pill ${status}`}>
                        {status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </CommonModal>
      )}

      {showImportModal && (
        <CommonModal
          onClose={() => setShowImportModal(false)}
          title={
            <>
              <i className="fa-solid fa-upload me-2"></i>
              Import Employees
            </>
          }
          overlayClassName="modal-overlay"
          contentClassName="modal-container"
          headerClassName="modal-header"
          bodyClassName="modal-body"
          footerClassName="modal-footer"
          closeButtonClassName="modal-close"
          footer={
            <CommonFormActions
              className="modal-footer"
              cancelLabel="Cancel"
              submitLabel={
                importing ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin me-1"></i>{" "}
                    Importing...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-upload me-1"></i> Import
                  </>
                )
              }
              onCancel={() => setShowImportModal(false)}
              onSubmit={handleImport}
              submitType="button"
              submitDisabled={importing}
            />
          }
        >
              {/* Download template */}
              <div className="import-template-note">
                <i className="fa-solid fa-circle-info"></i>
                &nbsp;Please use the correct Excel template.&nbsp;
                <a
                  href="/templates/employee-import-template.xlsx"
                  download
                  className="template-link"
                >
                  <i className="fa-solid fa-download me-1"></i>
                  Download Template
                </a>
              </div>

              {/* Column order hint */}
              <div className="import-columns-hint">
                <p className="hint-title">Expected Column Order:</p>
                <div className="hint-cols">
                  {[
                    "First Name",
                    "Middle Name",
                    "Last Name",
                    "Email",
                    "Phone",
                    "Gender",
                    "Marital Status",
                    "Nationality",
                    "Birth Date",
                    "Father's Name",
                    "Mother's Name",
                    "Spouse Name",
                    "Address",
                    "Job Title",
                    "Employment Status",
                    "Designation",
                    "Joining Date",
                  ].map((col, i) => (
                    <span key={i} className="hint-col">
                      <strong>{i + 1}.</strong> {col}
                    </span>
                  ))}
                </div>
              </div>

              {/* File picker */}
              <div className="form-group mt-3">
                <label>
                  Select File <span className="required">*</span>
                </label>
                <input
                  type="file"
                  className="form-control"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => {
                    setImportFile(e.target.files[0] || null);
                    setImportError("");
                  }}
                />
                <small className="text-muted">
                  Accepted formats: .xlsx, .xls, .csv
                </small>
              </div>

              {/* Error message */}
              {importError && (
                <div className="import-error">
                  <i className="fa-solid fa-triangle-exclamation me-1"></i>
                  {importError}
                </div>
              )}
        </CommonModal>
      )}

      {showAssignRoleModal && (
        <CommonModal
          onClose={() => setShowAssignRoleModal(false)}
          title="Assign Access Role"
          overlayClassName="modal-overlay"
          contentClassName="modal-container"
          headerClassName="modal-header"
          bodyClassName="modal-body"
          footerClassName="modal-footer"
          closeButtonClassName="modal-close"
          footer={
            <CommonFormActions
              className="modal-footer"
              cancelLabel="Cancel"
              submitLabel="Save Role"
              onCancel={() => setShowAssignRoleModal(false)}
              onSubmit={handleAssignRole}
              submitType="button"
            />
          }
        >
          <div className="form-group">
            <label>Employee</label>
            <input
              type="text"
              className="form-control"
              value={
                selectedRoleEmployee
                  ? `${selectedRoleEmployee.first_name} ${selectedRoleEmployee.last_name}`
                  : ""
              }
              readOnly
            />
          </div>

          <div className="form-group mt-3">
            <label>Access Role</label>
            <select
              className="form-control"
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
            >
              <option value="">Default Employee Access</option>
              {availableRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        </CommonModal>
      )}
    </MainLayout>
  );
}

export default EmployeeList;
